---
title: "PIN access and roles"
slug: "pin-access-and-roles"
category: "admin"
description: "Give helpers just enough access to lend and return, without exposing settings or pupil data."
tags: ["pin", "roles", "access", "security", "helpers"]
order: 2
---

BookBurrow isn't a password-heavy app — helpers shouldn't have to remember a username and a ten-character password just to scan books. Instead, access is controlled by a **four-digit PIN** and a handful of clearly named roles. This guide explains the roles, shows how to set PINs, and offers a sensible default policy for a primary school.

## The three roles

<Callout variant="info" title="Roles at a glance">
- **Helper** — can do day-to-day lending and returns, and search the catalogue. Cannot see pupil personal data beyond name + year group, and cannot open Settings.
- **Teacher** — everything Helper can do, plus add/edit books and borrowers, run reports, and handle lost/damaged books.
- **Admin** — everything, including Settings, backups, licence management, and year-end.
</Callout>

Each role has its own PIN (or optionally, each named person has their own PIN within a role). Switching between roles takes one keypress: type the PIN, hit Enter, you're in.

## A sensible default policy for primary schools

- **Helper PIN**: shared by parent helpers and TAs, written on a post-it inside the library cupboard. Low sensitivity — the worst someone could do with it is lend a book to the wrong pupil. Change it whenever the mix of helpers changes.
- **Teacher PIN**: known only to teaching staff. This is the PIN a teacher types when they need to check a pupil's loan history or fix a mistake.
- **Admin PIN**: known only to the librarian / IT coordinator / head of the library. Used sparingly, for settings changes and big operations.

For secondary schools, it's more common to give each named member of staff their own PIN so reports can show "who did what".

## Setting PINs

<Steps>
  <Step title="Open Settings → Security">
    The admin PIN is required here. On a brand-new install, the default admin PIN is whatever you set when you first opened the app.
  </Step>
  <Step title="Click Change PIN next to any role">
    Type the new four-digit PIN and confirm it.
  </Step>
  <Step title="Optionally, enable per-person PINs">
    Tick **Named helpers**, then add helpers one by one with their own PINs. Loans they process get tagged with their name in reports.
  </Step>
</Steps>

## Locking and unlocking the app

BookBurrow has two states: **locked** (helper mode) and **unlocked** (whatever role's PIN you last typed). The state is shown in the bottom-right of the app:

- **Helper mode**: just Lend, Return, and Search. Most of the time this is all you want.
- **Teacher mode / Admin mode**: all tabs visible.

The app auto-locks back to Helper mode after **10 minutes of inactivity**, or immediately when you click the lock icon in the bottom-right. This protects against the situation where a teacher opens Admin mode to fix something, then walks away and forgets.

## Forgotten PINs

If a helper or teacher forgets their PIN, an Admin can reset it from Settings → Security → Reset PIN. No drama.

If the **Admin PIN** is lost and there's no other Admin, you can recover it from the web in under a minute:

<Steps>
  <Step title="Click 'Forgot PIN?' on the BookBurrow PIN screen">
    BookBurrow will show a one-day challenge code that looks like `FROG-4271`. Leave that screen open.
  </Step>
  <Step title="Visit the PIN recovery page in your browser">
    Go to [bookburrow.uk/pin-recovery](/pin-recovery) on any computer with internet access.
  </Step>
  <Step title="Enter your licence key and the challenge code">
    The page verifies you have physical access to the library computer *and* proof of purchase.
  </Step>
  <Step title="Type the returned response code back into BookBurrow">
    BookBurrow confirms the code and lets you set a new admin PIN.
  </Step>
</Steps>

The challenge-response is secured by a shared secret and is bound to your licence, so nobody can use this page to reset a PIN on someone else's install.

There's a short version in the [forgotten-PIN FAQ entry](/help/faq#forgot-pin).

## What PINs don't protect

PINs are about preventing casual mistakes and keeping helpers in their lane — they're not high-security passwords. Someone with physical access to the computer could always open the database file directly. If you're worried about that level of threat, the answer is:

- Lock the library computer when unattended (the Windows lock screen, not just BookBurrow).
- Store the database on a location that's protected by your school's normal access controls.
- Consider full-disk encryption (BitLocker on Windows) — standard on managed school devices.

For 99% of schools, a four-digit PIN and a locked room at night are more than enough.

PINs are also what protect your [backup folder location](/help/guides/configuring-automatic-backups) from being changed to somewhere sensible by someone who meant well but wasn't thinking — another reason to keep the admin PIN to a small group.
